import * as React from 'react';

import cx from 'classnames';

import Checkbox from 'components/Checkbox';
// @ts-ignore
import List from 'components/List';

import './styles/CheckboxList.scss';

/**
 * ## Use a `CheckboxList` to easily and consistently lay out several [Checkbox](/internal/components/Checkbox)es.
 *
 *
 * ```example
 * <CheckboxList
 *   options={
 *     new Map([
 *       ['english', 'English'],
 *       ['history', 'History'],
 *       ['math', 'Math'],
 *       ['science', 'Science'],
 *     ])
 *   }
 *   onChange={checked => window.alert('Checked!')}
 * />
 * ```
 *
 * ```example
 * <CheckboxList
 *   checkboxAlignment="right"
 *   options={
 *     new Map([
 *       ['english', 'English'],
 *       ['history', 'History'],
 *       ['math', 'Math'],
 *       ['science', 'Science'],
 *     ])
 *   }
 * />
 * ```
 *
 * ```example
 * <CheckboxList
 *   direction="horizontal"
 *   options={
 *     new Map([
 *       ['english', 'English'],
 *       ['history', 'History'],
 *       ['math', 'Math'],
 *       ['science', 'Science'],
 *     ])
 *   }
 * />
 * ```
 *
 * ```example
 * <CheckboxList
 *   options={
 *     new Map([
 *       ['english', 'English'],
 *       ['history', 'History'],
 *       ['math', 'Math'],
 *       ['science', 'Science'],
 *     ])
 *   }
 *   showRowDividers
 * />
 * ```
 */
export type CheckboxListOptions =
  | Map<string, string | React.ReactNode>
  | { [value: string]: string | React.ReactNode }
  | (string)[];

export type Props = {
  /** Checked values */
  checked?: (any)[];
  /** Checked values for uncontrolled inputs */
  defaultChecked?: (any)[];
  /** Native */
  disabled?: (any)[];
  /** Native */
  name?: string;
  /**
   * Key-value pairs where the key is the value of the option. When using an array, the key and
   * value are the same.
   */
  options: CheckboxListOptions;
  /** Callback when checked state changes, called with an array of checked values */
  onChange?: (values: any) => void;
  eventElementName?: string;
  nonTextLabel?: boolean;
  className?: string;
};

type DefaultProps = {
  /** Checkbox input & label order */
  checkboxAlignment: 'default' | 'left' | 'right';
  /** Checkbox list orientation */
  direction: 'vertical' | 'horizontal' | 'columns';
  showRowDividers: boolean;
};

type State = {
  initialChecked: (any)[];
  checked: (any)[];
};

class CheckboxList extends React.Component<Props & DefaultProps, State> {
  static defaultProps: DefaultProps = {
    checkboxAlignment: 'default',
    showRowDividers: false,
    direction: 'vertical',
  };

  state = {
    initialChecked: this.props.defaultChecked || [],
    checked: Array.from(this.props.checked || this.props.defaultChecked || []),
  };

  componentWillReceiveProps(nextProps: Props) {
    if (this.props.checked !== nextProps.checked && nextProps.checked) {
      this.setState({ checked: Array.from(nextProps.checked) });
    }
  }

  getValue = () => {
    return this.state.checked;
  };

  handleChange = (value: string | number) => {
    const { checked } = this.state;
    const index = checked.indexOf(value);
    if (index >= 0) {
      checked.splice(index, 1);
    } else {
      checked.push(value);
    }
    this.setState({ checked });

    if (this.props.onChange) {
      this.props.onChange(checked);
    }
  };

  renderCheckbox = (value: string | number, label: React.ReactNode) => {
    const {
      checkboxAlignment,
      checked,
      direction,
      disabled,
      eventElementName,
      name,
      nonTextLabel,
      showRowDividers,
    } = this.props;
    // By default, the label is given as a child of the Checkbox
    // which renders it to the right of the checkbox input
    // If we want to float the checkbox input, we have to move the
    // label outside of the Checkbox component
    const labelAsChildOfCheckbox = checkboxAlignment === 'default';

    return (
      <li
        key={value}
        className={cx('CheckboxList-item', {
          'CheckboxList-item--left': checkboxAlignment === 'left',
          'CheckboxList-item--right': checkboxAlignment === 'right',
          'CheckboxList-item--horizontal': direction === 'horizontal',
          'CheckboxList-item--columns': direction === 'columns',
          'CheckboxList-item--divided': showRowDividers,
        })}>
        <Checkbox
          checked={checked && checked.includes(value)}
          defaultChecked={checked ? undefined : this.state.initialChecked.includes(value)}
          disabled={disabled && disabled.includes(value)}
          name={name}
          nonTextLabel={nonTextLabel}
          value={value}
          onChange={() => this.handleChange(value)}
          eventElementName={eventElementName}>
          {labelAsChildOfCheckbox && label}
        </Checkbox>
        {!labelAsChildOfCheckbox && label}
      </li>
    );
  };

  render() {
    const { className, onChange, options, showRowDividers, ...listProps } = this.props; // eslint-disable-line
    let items: React.ReactElement[] = [];

    if (options instanceof Map) {
      options.forEach((label, value) => {
        items.push(this.renderCheckbox(value, label));
      });
    } else if (Array.isArray(options)) {
      items = options.map((label, index) => {
        return this.renderCheckbox(index, label);
      });
    } else {
      items = Object.keys(options).map(value => {
        return this.renderCheckbox(value, options[value]);
      });
    }

    return (
      <List {...listProps} className={cx('CheckboxList', className)}>
        {items}
      </List>
    );
  }
}

export default CheckboxList;
